import { Card, Divider } from "@drone-js/core";
import { TaxInformationController } from "apps/oneOnboarding/contract/TaxInformationController";
import { useOnboardingPageContextAtomValue } from "apps/oneOnboarding/onboardingPageContextAtom";
import { useStaticDataAtomValue } from "apps/oneOnboarding/staticDataAtom";
import { getOnboardingPropertyContextFromMap } from "apps/oneOnboarding/usePropertyContext";
import type { TaxConfig } from "libs/codegen/property-onboarding";
import { Fragment, type FunctionComponent, useMemo } from "react";
import { type FieldArrayWithId, useFormContext } from "react-hook-form";
import type { BulkPropertyFormData } from "../BulkPropertyFormData";
import { TaxFieldsSyncer } from "./TaxFieldsSyncer";

interface Props {
    fields: FieldArrayWithId<BulkPropertyFormData, "propertyDataList", "id">[];
}

export const TaxFields: FunctionComponent<Props> = ({ fields }) => {
    const { setValue } = useFormContext<BulkPropertyFormData>();
    const {
        countriesList,
        cmsData: {
            publish: { taxInformationForCountry },
        },
    } = useStaticDataAtomValue();
    const { propertiesContext } = useOnboardingPageContextAtomValue();

    // Group properties by country ID and pick the first one as the primary (to show the form)
    const countryGroupedFields = useMemo(() => {
        const countryMap = new Map<
            number,
            {
                primaryIndex: number;
                primaryId: string;
                countryName: string;
                taxConfiguration: TaxConfig;
                allIndices: number[];
            }
        >();

        fields.forEach((field, i) => {
            const propertyId = field.propertyId;
            const countryId = field.country.id;

            const propertyTaxConfiguration = getOnboardingPropertyContextFromMap(
                propertiesContext,
                propertyId,
                field.onboardingId,
            )?.taxConfiguration;

            // Skip if not eligible for tax form
            if (propertyTaxConfiguration === undefined || propertyTaxConfiguration === null) return;

            if (!countryMap.has(countryId)) {
                // First property for this country - it will be the primary (form shown)
                countryMap.set(countryId, {
                    primaryIndex: i,
                    primaryId: field.id,
                    countryName: countriesList.find((country) => country.id === countryId)?.name ?? "",
                    taxConfiguration: propertyTaxConfiguration,
                    allIndices: [i],
                });
            } else {
                // Additional property with same country - add to sync list
                countryMap.get(countryId)?.allIndices.push(i);
            }
        });

        return Array.from(countryMap.values());
    }, [fields, propertiesContext]);

    return (
        <>
            {countryGroupedFields.map(({ primaryIndex, primaryId, taxConfiguration, allIndices, countryName }, idx) => {
                const controller = (
                    <TaxInformationController
                        title={taxInformationForCountry.replace("{0}", countryName)}
                        fieldPrefix={`propertyDataList.${primaryIndex}`}
                        {...taxConfiguration}
                    />
                );

                return (
                    <Fragment key={primaryId}>
                        <Card type="non-interactive" variant="filled" backgroundColor="base.inset" p="16">
                            {controller}
                        </Card>
                        {/* Sync tax information from primary property to other properties with same country */}
                        {allIndices.length > 1 && (
                            <TaxFieldsSyncer
                                primaryIndex={primaryIndex}
                                syncTargetIndices={allIndices.filter((i) => i !== primaryIndex)}
                                setValue={setValue}
                            />
                        )}
                        {idx !== countryGroupedFields.length - 1 && (
                            <Divider direction="horizontal" color="base.subtle" />
                        )}
                    </Fragment>
                );
            })}
        </>
    );
};
